import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
} from "@mui/material";
import { useLanguage } from "../i18n/LanguageContext";

// ✅ MUI Icons (all available in v5)
import FavoriteIcon from "@mui/icons-material/Favorite";
import SpaIcon from "@mui/icons-material/Spa";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import EmojiNatureIcon from "@mui/icons-material/EmojiNature";
import MedicationIcon from "@mui/icons-material/Medication";
import OpacityIcon from "@mui/icons-material/Opacity";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import LocalDrinkIcon from "@mui/icons-material/LocalDrink";
import HealingIcon from "@mui/icons-material/Healing";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import WcIcon from "@mui/icons-material/Wc";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import CategoryIcon from "@mui/icons-material/Category"; // replacement for EcoIcon

// ====== ICON CARD COMPONENT ======
const IconCard = ({ label, icon }) => (
  <Card
    sx={{
      height: 150,
      borderRadius: 3,
      boxShadow: 2,
      transition: "0.3s",
      "&:hover": { boxShadow: 6, transform: "translateY(-5px)" },
    }}
  >
    <CardActionArea sx={{ height: "100%" }}>
      <CardContent
        sx={{
          textAlign: "center",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ color: "primary.main", mb: 1 }}>{icon}</Box>
        <Typography variant="body1" fontWeight={600}>
          {label}
        </Typography>
      </CardContent>
    </CardActionArea>
  </Card>
);

// ====== MAIN PAGE ======
const SolutionsPage = () => {
  const { t } = useLanguage();

  const SHOP_BY_RANGE = [
    { label: t("solutions.ranges.immunity"), icon: <HealthAndSafetyIcon fontSize="large" /> },
    { label: t("solutions.ranges.pureHerbs"), icon: <SpaIcon fontSize="large" /> },
    { label: t("solutions.ranges.hairCare"), icon: <EmojiNatureIcon fontSize="large" /> },
    { label: t("solutions.ranges.remedies"), icon: <HealingIcon fontSize="large" /> },
    { label: t("solutions.ranges.herbalJuices"), icon: <LocalDrinkIcon fontSize="large" /> },
    { label: t("solutions.ranges.healthWellness"), icon: <SelfImprovementIcon fontSize="large" /> },
    { label: t("solutions.ranges.diabetes"), icon: <MedicationIcon fontSize="large" /> },
    { label: t("solutions.ranges.coughCold"), icon: <LocalHospitalIcon fontSize="large" /> },
    { label: t("solutions.ranges.classicalRange"), icon: <CategoryIcon fontSize="large" /> },
    { label: t("solutions.ranges.chyawanprash"), icon: <FavoriteIcon fontSize="large" /> },
    { label: t("solutions.ranges.digestion"), icon: <RestaurantIcon fontSize="large" /> },
    { label: t("solutions.ranges.honey"), icon: <OpacityIcon fontSize="large" /> },
  ];

  const SHOP_BY_CATEGORY = [
    { label: t("solutions.categories.asav"), icon: <LocalPharmacyIcon fontSize="large" /> },
    { label: t("solutions.categories.churna"), icon: <SpaIcon fontSize="large" /> },
    { label: t("solutions.categories.chyawanprash"), icon: <FavoriteIcon fontSize="large" /> },
    { label: t("solutions.categories.coolingOil"), icon: <OpacityIcon fontSize="large" /> },
    { label: t("solutions.categories.goldRange"), icon: <CategoryIcon fontSize="large" /> },
    { label: t("solutions.categories.goldBati"), icon: <HealingIcon fontSize="large" /> },
    { label: t("solutions.categories.hairOil"), icon: <EmojiNatureIcon fontSize="large" /> },
    { label: t("solutions.categories.herbalJuices"), icon: <LocalDrinkIcon fontSize="large" /> },
    { label: t("solutions.categories.isabgol"), icon: <RestaurantIcon fontSize="large" /> },
    { label: t("solutions.categories.kasomrit"), icon: <LocalHospitalIcon fontSize="large" /> },
    { label: t("solutions.categories.patient"), icon: <HealthAndSafetyIcon fontSize="large" /> },
    { label: t("solutions.categories.proprietary"), icon: <MedicationIcon fontSize="large" /> },
    { label: t("solutions.categories.pureHerbs"), icon: <SpaIcon fontSize="large" /> },
    { label: t("solutions.categories.rasRasayan"), icon: <CategoryIcon fontSize="large" /> },
    { label: t("solutions.categories.rhuma"), icon: <FitnessCenterIcon fontSize="large" /> },
  ];

  const SHOP_BY_SOLUTIONS = [
    { label: t("solutions.solutionTypes.anywhere"), icon: <SpaIcon fontSize="large" /> },
    { label: t("solutions.solutionTypes.homeRemedies"), icon: <HealingIcon fontSize="large" /> },
    { label: t("solutions.solutionTypes.scienceBased"), icon: <LocalPharmacyIcon fontSize="large" /> },
    { label: t("solutions.solutionTypes.boneJoint"), icon: <FitnessCenterIcon fontSize="large" /> },
    { label: t("solutions.solutionTypes.cardiac"), icon: <FavoriteIcon fontSize="large" /> },
    { label: t("solutions.solutionTypes.coughCold"), icon: <LocalHospitalIcon fontSize="large" /> },
    { label: t("solutions.solutionTypes.influenza"), icon: <MedicationIcon fontSize="large" /> },
    { label: t("solutions.solutionTypes.liver"), icon: <HealingIcon fontSize="large" /> },
    { label: t("solutions.solutionTypes.mensWellness"), icon: <WcIcon fontSize="large" /> },
    { label: t("solutions.solutionTypes.piles"), icon: <LocalHospitalIcon fontSize="large" /> },
    { label: t("solutions.solutionTypes.respiratory"), icon: <SelfImprovementIcon fontSize="large" /> },
    { label: t("solutions.solutionTypes.skinCare"), icon: <SpaIcon fontSize="large" /> },
    { label: t("solutions.solutionTypes.stress"), icon: <SelfImprovementIcon fontSize="large" /> },
  ];

  return (
    <Box sx={{ backgroundColor: "#fafafa", minHeight: "100vh", py: 8 }}>
      <Container maxWidth="lg">
        {/* Main Title */}
        <Typography variant="h3" fontWeight={800} textAlign="center" mb={8}>
          {t("solutions.title")}
        </Typography>

        {/* Shop by Range */}
        <Typography variant="h5" fontWeight={700} mb={4}>
          {t("solutions.byRange")}
        </Typography>
        <Grid container spacing={3} mb={8}>
          {SHOP_BY_RANGE.map((item, i) => (
            <Grid item xs={6} sm={4} md={3} key={i}>
              <IconCard label={item.label} icon={item.icon} />
            </Grid>
          ))}
        </Grid>

        {/* Shop by Category */}
        <Typography variant="h5" fontWeight={700} mb={4}>
          {t("solutions.byCategory")}
        </Typography>
        <Grid container spacing={3} mb={8}>
          {SHOP_BY_CATEGORY.map((item, i) => (
            <Grid item xs={6} sm={4} md={3} key={i}>
              <IconCard label={item.label} icon={item.icon} />
            </Grid>
          ))}
        </Grid>

        {/* Shop by Solutions */}
        <Typography variant="h5" fontWeight={700} mb={4}>
          {t("solutions.bySolution")}
        </Typography>
        <Grid container spacing={3}>
          {SHOP_BY_SOLUTIONS.map((item, i) => (
            <Grid item xs={6} sm={4} md={3} key={i}>
              <IconCard label={item.label} icon={item.icon} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default SolutionsPage;
